import { useEffect } from "react";
import { IMAGES } from "../images/images"
import { Phone, Trash2, Home, Package, CheckCircle } from "lucide-react";
import { trackPhoneConversion } from "../utils/tracking";

const PHONE = "+359876995651";
const PHONE_DISPLAY = "Обадете се";

const categories = [
  {
    img: IMAGES.PochistvaneOne,
    title: "Почистване на Мазета",
    keyword: "почистване на мазета",
    desc: "Изчистваме мазета от натрупаните с годините вещи — стари мебели, боклуци, отпадъци. Събираме, товарим и извозваме всичко до официален изхвърлителен пункт. Работим бързо и чисто.",
    list: ["Събиране на отпадъци и стари вещи", "Товарене и извозване до сметище", "Физическо почистване на помещението", "Разглобяване на неизползваеми мебели"],
    testId: "cleaning-mazeta",
  },
  {
    img: IMAGES.PochistvaneTwo,
    title: "Почистване на Тавани",
    keyword: "почистване на тавани",
    desc: "Таванските помещения се натрупват с неизползвани вещи с годините. Нашият екип е обучен да работи безопасно на всяка височина — почистваме пространството бързо и без рискове.",
    list: ["Достъп и работа на всяка височина", "Извозване на натрупани вещи", "Разчистване след ремонт", "Пълно или частично разчистване"],
    testId: "cleaning-tavani",
  },
  {
    img: IMAGES.PochistvaneThree,
    title: "Изхвърляне на Стари Мебели",
    keyword: "изхвърляне на стари мебели",
    desc: "Освободете пространство от стари дивани, легла, шкафове и всякакви ненужни предмети. Разглобяваме мебелите при нужда, товарим ги и ги извозваме до сметище или рециклиращ пункт.",
    list: ["Разглобяване на мебели при нужда", "Пренасяне до транспортното средство", "Извозване до официален пункт", "Подходящ и за жилища, и за офиси"],
    testId: "cleaning-mebeli",
  },
];

export default function PochistvamePage() {
  useEffect(() => {
    document.title = "Почистване на мазета и тавани София | Изхвърляне на мебели — Хамали София";
    const setMeta = (name, content, property = false) => {
      const attr = property ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setCanonical = (url) => {
      let el = document.querySelector('link[rel="canonical"]');
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
      el.setAttribute("href", url);
    };
    setMeta("description", "Почистване на мазета, тавани и гаражи в София. Изхвърляне на стари мебели и боклуци до сметище. Бързо, изгодно и чисто. Обадете се: +359 876 995 651");
    setMeta("og:title", "Почистване на мазета и тавани София | Изхвърляне на мебели — Хамали София", true);
    setMeta("og:description", "Изчистваме мазета, тавани и гаражи от ненужни вещи и стари мебели в цяла София. Товарим и извозваме до официален пункт. Бързо и изгодно.", true);
    setMeta("og:url", "https://hamalski-sofia.com/pochistvane", true);
    setCanonical("https://hamalski-sofia.com/pochistvane");
  }, []);

  return (
    <div style={{ backgroundColor: "#FFFFFF", paddingTop: "68px" }}>

      {/* HERO — dark */}
      <section data-testid="cleaning-hero" style={{ position: "relative", minHeight: "70vh", display: "flex", alignItems: "center", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${IMAGES.PochistvaneHero})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="hero-overlay" />
        <div style={{ position: "relative", zIndex: 2, maxWidth: "1280px", margin: "0 auto", padding: "4rem 1.25rem", width: "100%" }}>
          <p style={{ color: "#2dcf0c", fontSize: "0.8rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "1rem" }}>Почистване — София</p>
          <h1 data-testid="cleaning-h1" style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 900, fontSize: "clamp(2.25rem, 6vw, 4.5rem)", lineHeight: 1.05, letterSpacing: "-0.02em", color: "#FFFFFF", textTransform: "uppercase", maxWidth: "750px", marginBottom: "1.5rem" }}>
            Почистване на Мазета и Тавани в{" "}
            <span style={{ color: "#2dcf0c" }}>София</span>
          </h1>
          <p style={{ color: "#d4d4d8", fontSize: "1.05rem", lineHeight: 1.7, maxWidth: "560px", marginBottom: "2rem" }}>
            Бързо и ефикасно изхвърляне на стари мебели, разчистване на ненужни вещи от мазета и тавани. Извозване до сметище — справяме се с всичко вместо вас!
          </p>
          <a href={`tel:${PHONE}`} data-testid="cleaning-call-btn" className="btn-call" style={{ padding: "1rem 2.25rem", fontSize: "1rem", borderRadius: "4px" }} onClick={trackPhoneConversion}>
            <Phone size={20} />
            <span>{PHONE_DISPLAY}</span>
          </a>
        </div>
      </section>

      {/* INTRO — white */}
      <section style={{ padding: "4rem 1.25rem", maxWidth: "900px", margin: "0 auto" }}>
        <p style={{ fontSize: "1.075rem", color: "#374151", lineHeight: 1.85, borderLeft: "3px solid #2dcf0c", paddingLeft: "1.5rem" }}>
          <strong style={{ color: "#0A0A0A" }}>Хамали София</strong> предлага услуга за почистване на мазета, тавани и гаражи в цялото София. Осъзнаваме, че почистването на тези помещения е мръсна и изморителна задача — поемаме я изцяло вместо вас. Нашият екип е обучен и оборудван да се справи с всякакъв обем вещи.
        </p>
      </section>

      {/* SERVICE CATEGORIES — alternating */}
      <section data-testid="cleaning-categories" style={{ padding: "2rem 1.25rem 5rem", maxWidth: "1280px", margin: "0 auto" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "4rem" }}>
          {categories.map((cat, i) => (
            <div key={cat.title} data-testid={cat.testId}
              style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "2.5rem", alignItems: "center" }}>
              <div className="img-zoom" style={{ borderRadius: "8px", overflow: "hidden", order: i % 2 !== 0 ? 2 : 1, minHeight: "280px", boxShadow: "0 4px 24px rgba(0,0,0,0.1)" }}>
                <img src={cat.img} alt={cat.title} style={{ width: "100%", height: "320px", objectFit: "cover" }} />
              </div>
              <div style={{ order: i % 2 !== 0 ? 1 : 2 }}>
                <p style={{ color: "#2dcf0c", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "0.75rem" }}>{cat.keyword}</p>
                <h2 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 900, fontSize: "clamp(1.5rem, 3vw, 2rem)", color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "-0.02em", marginBottom: "1rem" }}>{cat.title}</h2>
                <p style={{ color: "#6B7280", fontSize: "0.925rem", lineHeight: 1.75, marginBottom: "1.5rem" }}>{cat.desc}</p>
                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 1.75rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  {cat.list.map((item) => (
                    <li key={item} style={{ display: "flex", alignItems: "center", gap: "0.625rem", color: "#374151", fontSize: "0.875rem" }}>
                      <CheckCircle size={15} color="#2dcf0c" style={{ flexShrink: 0 }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW WE WORK — light grey */}
      <section data-testid="cleaning-process" style={{ backgroundColor: "#F8F8FA", borderTop: "1px solid rgba(0,0,0,0.06)", padding: "4rem 1.25rem" }}>
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <h2 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 900, fontSize: "clamp(1.5rem, 3vw, 2.25rem)", color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "-0.02em", textAlign: "center", marginBottom: "3rem" }}>
            Как работим
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1.5rem" }}>
            {[
              { icon: Phone, label: "1. Обаждане", desc: "Звъните ни и описвате какво трябва да почистим и обема на работата." },
              { icon: Home, label: "2. Оглед", desc: "Безплатен оглед на помещението за точна оценка на обема и цената." },
              { icon: Trash2, label: "3. Почистване", desc: "Екипът пристига, събира, товари и извозва всичко." },
              { icon: CheckCircle, label: "4. Готово!", desc: "Помещението е чисто и освободено — без остатъчни отпадъци." },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} style={{ textAlign: "center", padding: "1.75rem 1rem", backgroundColor: "#FFFFFF", borderRadius: "8px", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
                  <div style={{ width: 52, height: 52, backgroundColor: "rgba(45,207,12,0.08)", border: "1px solid rgba(45,207,12,0.2)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem" }}>
                    <Icon size={22} color="#2dcf0c" />
                  </div>
                  <h3 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800, fontSize: "0.9rem", color: "#0A0A0A", textTransform: "uppercase", marginBottom: "0.5rem" }}>{s.label}</h3>
                  <p style={{ color: "#6B7280", fontSize: "0.85rem", lineHeight: 1.65 }}>{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cleaning-cta" style={{ padding: "4rem 1.25rem", textAlign: "center", backgroundColor: "#F0FDF4", borderTop: "1px solid rgba(45,207,12,0.2)" }}>
        <Package size={40} color="#2dcf0c" style={{ marginBottom: "1rem" }} />
        <h2 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 900, fontSize: "clamp(1.5rem, 3.5vw, 2.5rem)", color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "-0.02em", marginBottom: "1rem" }}>
          Пречистете пространството! <span style={{ color: "#2dcf0c" }}>Обадете се!</span>
        </h2>
        <p style={{ color: "#6B7280", marginBottom: "2rem", fontSize: "1rem" }}>
          Почистване на мазета и тавани в София. Безплатна оценка по телефона или с оглед на място.
        </p>
        <a href={`tel:${PHONE}`} data-testid="cleaning-cta-btn" className="btn-call" style={{ padding: "1.1rem 2.5rem", fontSize: "1.1rem", borderRadius: "4px" }} onClick={trackPhoneConversion}>
          <Phone size={22} />
          <span>{PHONE_DISPLAY}</span>
        </a>
      </section>
    </div>
  );
}
